import React, { useState } from 'react';
import I18n from 'i18n-js';

import { RCMenu, RCChip } from '@reverbdotcom/cadence/components';
import { useQuery } from '@reverbdotcom/commons/src/useQuery';

import BumpModalV2WithController, { BumpButtonV2 } from './footer_buttons/BumpButtonV2';
import { bumpRatePercentToDecimal } from '../bump/bump_helpers';
import { AutoOfferModalButtonV2, AutoOfferModalV2 } from './footer_buttons/AutoOfferModalButtonV2';
import SalesModalButton from './footer_buttons/sales/SalesModalButton';
import { AffirmZeroPercentButton, AffirmZeroPercentModal } from './footer_buttons/affirm_zero_percent_financing/AffirmZeroPercentButton';
import SellerListingStats from './SellerListingStats';
import SellerListingInsightStats from './SellerListingInsightStats';
import SalesModal, { AVAILABLE_SALES_MEMBERSHIPS_QUERY } from './footer_buttons/sales/SalesModal';
import { Listing, CoreMyListingAvailableSalesMemberships } from '@reverbdotcom/commons/src/gql/graphql';
import SellerListingCardInsightsActionMenu from './SellerListingCardInsightsActionMenu';
import { hasInsightRecommendation } from './insightHelpers';
import { LIVE, DRAFT } from './toolbarHelpers';

export const SELLER_SITE = 'seller_site';

interface IExternalProps {
  listing: Listing;
}

interface IFooterProps extends IExternalProps {
  isMobile?: boolean;
  inInsightsCardVariant?: boolean;
}

export function isListingBumpEligible(listing) {
  const isListingLive = listing.state === LIVE;
  const isSellerSiteExclusive = listing.exclusiveChannel == SELLER_SITE;

  return isListingLive && !isSellerSiteExclusive;
}

export function SellerFeatureButtons({ listing }: IExternalProps) {
  const [showBumpModal, setShowBumpModal] = useState(false);
  const [showSalesModal, setShowSalesModal] = useState(false);
  const [showAutoOfferModal, setShowAutoOfferModal] = useState(false);
  const [showAffirmZeroPercentModal, setShowAffirmZeroPercentModal] = useState(false);

  const isListingLive = listing.state === LIVE;
  const isSellerSiteExclusive = listing.exclusiveChannel == SELLER_SITE;

  const bumpRate = listing.bumpRate?.rate || 0;
  const isBumpEligible = isListingBumpEligible(listing);
  const isAutoOffersEnabled = listing.autoOffers.id !== '';
  const showSaleButton = isListingLive && !isSellerSiteExclusive && listing.shop.canCurateSales;
  const showAffirmZeroPercentButton = (listing.affirmZeroPercentFinancingConfiguration.isEligible || listing.affirmZeroPercentFinancingConfiguration.isEnabled) && isListingLive;
  const isAffirmEnabled = listing.affirmZeroPercentFinancingConfiguration.isEnabled;

  const {
    data: salesData,
    loading: salesLoading,
  } = useQuery<CoreMyListingAvailableSalesMemberships.Query>(AVAILABLE_SALES_MEMBERSHIPS_QUERY, {
    ssr: false,
    fetchPolicy: 'network-only',
    variables: {
      listingId: listing.id,
    },
    errorPolicy: 'all',
  });

  const doNotRender = !isBumpEligible && !showSaleButton && !listing.isAutoOfferEligible && !showAffirmZeroPercentButton;

  if (doNotRender) return null;

  return (
    <>
      <RCMenu
        anchor={
          <RCChip>
            {I18n.t('discovery.listingsManagement.sellerListingCard.attractBuyers')}
          </RCChip>
        }
        position="bottom-end"
      >
        {isBumpEligible && (
          <div className="tablet-width-100">
            <BumpButtonV2
              listing={listing}
              setShowModal={setShowBumpModal}
            />
          </div>
        )}
        {showSaleButton && (
          <SalesModalButton
            loading={salesLoading}
            data={salesData}
            setIsModalOpen={setShowSalesModal}
          />
        )}
        {listing.isAutoOfferEligible && (
          <div className="tablet-width-100">
            <AutoOfferModalButtonV2
              listingId={listing.id}
              offerActive={isAutoOffersEnabled}
              setShowModal={setShowAutoOfferModal}
            />
          </div>
        )}
        {showAffirmZeroPercentButton && (
          <div className="tablet-width-100">
            <AffirmZeroPercentButton
              listingId={listing.id}
              isAffirmEnabled={isAffirmEnabled}
              setShowModal={setShowAffirmZeroPercentModal}
            />
          </div>
        )}
      </RCMenu>

      <BumpModalV2WithController
        listingId={listing.id}
        bid={bumpRatePercentToDecimal(bumpRate)}
        showModal={showBumpModal}
        setShowModal={setShowBumpModal}
        listing={listing}
      />
      <SalesModal
        data={salesData}
        isModalOpen={showSalesModal}
        onModalClose={() => setShowSalesModal(false)}
      />
      <AutoOfferModalV2
        showModal={showAutoOfferModal}
        setShowModal={setShowAutoOfferModal}
        listingId={listing.id}
      />
      <AffirmZeroPercentModal
        isAffirmEnabled={isAffirmEnabled}
        listingId={listing.id}
        showModal={showAffirmZeroPercentModal}
        setShowModal={setShowAffirmZeroPercentModal}
      />
    </>
  );
}

export default function SellerListingCardFooter({ listing, isMobile = false, inInsightsCardVariant = false }: IFooterProps) {

  // if listing state is draft, SellerListingStats does not render
  const justifyClass = listing.state === DRAFT ? 'fx-justify-end' : 'fx-justify-between';

  return (
    <div className="seller-listing-card-footer">
      <div className={`d-flex ${justifyClass}`}>
        {inInsightsCardVariant ? <SellerListingInsightStats listing={listing} /> : <SellerListingStats listing={listing} />}
        {!inInsightsCardVariant && <SellerFeatureButtons listing={listing} />}
        {isMobile && inInsightsCardVariant && <SellerListingCardInsightsActionMenu listing={listing} hasRecommendation={hasInsightRecommendation(listing)} />}
      </div>
    </div>
  );
}
